import { useState, useEffect } from 'react';

export default function StickyReserveCTA() {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const targetDate = new Date('2026-03-31T23:59:59');
    const update = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-500 ease-out ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      style={{
        background: 'rgba(8, 8, 8, 0.9)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
      }}
    >
      {/* ── デスクトップ ── */}
      <div className="hidden md:flex items-center justify-between max-w-6xl mx-auto px-8 py-3 gap-6">

        {/* 左：商品名 + 価格 */}
        <div className="flex items-center gap-5 flex-shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-medium leading-none mb-1">MeVER Chair Pro v2.0</span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-white leading-none tabular-nums">¥89,650</span>
              <span className="text-xs text-white/40 line-through tabular-nums">¥128,700</span>
              <span className="inline-flex items-center px-2 py-0.5 bg-purple-600 text-white text-[10px] font-black rounded-full leading-none">30%OFF</span>
            </div>
            {/* Paidy 分割払い */}
            <div className="flex items-center gap-1.5 mt-1.5">
              <img
                src="https://static.readdy.ai/image/a70e5a8c420a179c893b7b588530b5c7/34aa351f4c9ed77898376e331745f99c.svg"
                alt="Paidy"
                className="h-5 w-auto object-contain flex-shrink-0"
                style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }}
              />
              <span className="text-[11px] text-white/50">なら</span>
              <span className="text-[13px] font-black text-white/80">月々7,470円</span>
              <span className="text-[11px] text-white/50">×12回から</span>
              <span className="text-[10px] text-white/35 font-medium">分割手数料無料</span>
            </div>
          </div>
          <div className="w-px h-10 bg-white/10 flex-shrink-0" />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 text-white/50 text-[10px] font-semibold rounded-full whitespace-nowrap">
              <i className="ri-shield-check-line text-xs text-purple-400"></i>
              5年保証
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 text-white/50 text-[10px] font-semibold rounded-full whitespace-nowrap">
              <i className="ri-truck-line text-xs text-purple-400"></i>
              送料無料
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 text-white/50 text-[10px] font-semibold rounded-full whitespace-nowrap">
              <i className="ri-fire-line text-xs text-red-400"></i>
              各色50脚限定
            </span>
          </div>
        </div>

        {/* 中央：カウントダウン */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <span className="text-[9px] tracking-[0.3em] uppercase text-white/40 font-semibold">キャンペーン終了まで</span>
          <div className="flex items-center gap-1">
            {[
              { value: pad(countdown.days), label: '日' },
              { value: pad(countdown.hours), label: '時' },
              { value: pad(countdown.minutes), label: '分' },
              { value: pad(countdown.seconds), label: '秒' },
            ].map((item, idx) => (
              <div key={item.label} className="flex items-center gap-1">
                {idx > 0 && <span className="text-white/30 text-sm font-black">:</span>}
                <div className="flex flex-col items-center">
                  <span
                    className="text-xl font-black tabular-nums leading-none px-1.5 py-0.5 rounded-md"
                    style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa' }}
                  >
                    {item.value}
                  </span>
                  <span className="text-[8px] text-white/40 mt-0.5">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右：CTAボタン */}
        <a
          href="https://mever.design/products/mever-chair-pro-ver2"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2.5 px-8 py-3 rounded-full font-bold text-sm tracking-wide whitespace-nowrap cursor-pointer flex-shrink-0 transition-all duration-300 hover:scale-[1.03] bg-purple-600 hover:bg-purple-700 text-white"
        >
          <span>今すぐ予約する</span>
          <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
            <i className="ri-arrow-right-line text-base"></i>
          </span>
        </a>
      </div>

      {/* ── モバイル ── */}
      <div
        className="md:hidden flex flex-col px-3 pt-2 gap-1.5"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        {/* 上段：価格 + カウントダウン */}
        <div className="flex items-center justify-between gap-2">
          {/* 価格 */}
          <div className="flex items-baseline gap-1.5">
            <span className="text-[1.15rem] font-black text-white leading-none tabular-nums">¥89,650</span>
            <span className="text-[10px] text-white/35 line-through tabular-nums">¥128,700</span>
            <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-400 text-gray-900 text-[9px] font-black rounded-full leading-none whitespace-nowrap">30%OFF</span>
          </div>

          {/* カウントダウン */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[9px] text-white/35 font-semibold whitespace-nowrap">終了まで</span>
            <div className="flex items-center gap-0.5">
              {[
                { value: pad(countdown.hours), label: 'h' },
                { value: pad(countdown.minutes), label: 'm' },
                { value: pad(countdown.seconds), label: 's' },
              ].map((item, idx) => (
                <div key={item.label} className="flex items-center gap-0.5">
                  {idx > 0 && <span className="text-white/25 text-[10px] font-black">:</span>}
                  <span className="text-[13px] font-black tabular-nums text-amber-300 leading-none">{item.value}</span>
                  <span className="text-[8px] text-white/35 leading-none">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTAボタン */}
        <a
          href="https://mever.design/products/mever-chair-pro-ver2"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative w-full flex items-center justify-center gap-2 rounded-xl font-black text-[0.9rem] tracking-wide whitespace-nowrap cursor-pointer overflow-hidden active:scale-[0.97] transition-all duration-200"
          style={{
            minHeight: '44px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
            color: '#1a0a00',
            boxShadow: '0 0 20px rgba(245,158,11,0.45), 0 3px 10px rgba(0,0,0,0.35)',
          }}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-active:translate-x-[100%] transition-transform duration-500" />
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-shopping-bag-3-fill text-sm"></i>
          </span>
          <span>今すぐ予約する</span>
          <span className="w-4 h-4 flex items-center justify-center transition-transform duration-300 group-active:translate-x-1">
            <i className="ri-arrow-right-line text-sm"></i>
          </span>
        </a>
      </div>
    </div>
  );
}